import { useState } from 'react';
import { useParams } from 'react-router-dom';

import useGetContractDetails from '@/hooks/contract/useGetContractDetails.hook';
import { EDITABLE_STATUS_KEYS } from '@/config/statuses.config';
import useGetContractLinesFromCache from '@/hooks/contract/useGetContractLinesFromCache.hook';
import { useBulkUpdateProRataEndDate } from '@/pages/ContractDetails/ContractLines/hooks/useBulkUpdateProRataEndDate';
import ContractHeaderDatePickerItem from
  '../../../../../components/common/ContractHeaderItem/ContractHeaderDatePickerItem';
import OptionButton from '../../../../../components/common/OptionButton/OptionButton';
import AgModalWindow from '../../../../../components/common/ModalWindow/AgModalWindow';
import AgConfirmationModal from '../../../../../components/common/ModalWindow/AgConfirmationModal';
import ActionButtonsWrapper from
  '../../../../../components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';
import Button, { ButtonStyleType } from '../../../../../components/common/Button/Button';
import EditIcon from '../../../../../components/common/OptionButton/icons/Edit/EditIcon';
import {
  useSelectedLines
} from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';

export default function BulkUpdateContractLineProRataEndDateContainer() {
  const { contractId }: { contractId: UUID } = useParams();
  const { contract } = useGetContractDetails(contractId);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [newProRataEndDate, setNewProRataEndDate] = useState<Date | undefined>(undefined);

  const { bulkUpdateProRataEndDate } = useBulkUpdateProRataEndDate(contractId);
  const { contractHasActiveSubscriptionLines } = useGetContractLinesFromCache(contractId);
  const selectedLineRows = useSelectedLines();

  const isVisible = contract &&
    contractHasActiveSubscriptionLines &&
    EDITABLE_STATUS_KEYS.includes(contract.status.status) &&
    selectedLineRows.length > 0;

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };


  const handleConfirm = () => {
    setShowModal(false);
    setShowConfirmationModal(true);
  };

  const onSubmit = () => {
    bulkUpdateProRataEndDate({
      contractId: contractId,
      contractLineIds: selectedLineRows.map(cl => cl.id),
      proRataEndDate: newProRataEndDate
    });

    setShowConfirmationModal(false);
    setNewProRataEndDate(undefined);
  };

  const onCancel = () => {
    setShowConfirmationModal(false);
    setNewProRataEndDate(undefined);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <OptionButton onClick={handleOpenModal} Icon={<EditIcon />}>
        Update Pro Rata End Date
      </OptionButton>

      {showModal && (
        <AgModalWindow
          isOpen={true}
          onClose={handleCloseModal}
          title="Update Pro Rata End Date"
          contentStyle={{ minWidth: '500px', maxWidth: '600px' }}
        >
          <div style={{ marginBottom: '20px' }}>
            <ContractHeaderDatePickerItem
              label="New Pro Rata End Date"
              value={newProRataEndDate}
              disabled={false}
              clearButton={true}
              onChange={({ value }) => {
                setNewProRataEndDate(value || undefined);
              }}
            />
          </div>

          <ActionButtonsWrapper
            leftButton={(
              <Button
                buttonStyleType={ButtonStyleType.SECONDARY}
                onClick={handleCloseModal}
              >
                Cancel
              </Button>
            )}
            rightButton={(
              <Button
                buttonStyleType={ButtonStyleType.PRIMARY}
                onClick={handleConfirm}
              >
                Confirm
              </Button>
            )}
          />
        </AgModalWindow>
      )}

      <AgConfirmationModal
        isOpen={showConfirmationModal}
        onClose={onCancel}
        onConfirm={onSubmit}
        title="Confirm Pro Rata End Date Update"
      >
        Are you sure you want to update the Pro Rata End Date for the selected contract lines?
        <br />
        This will change the billing cycles for these lines.
      </AgConfirmationModal>
    </>
  );
}
